import { apiClient } from './apiClient';
import {
  ApsCommand,
  ApsEnvironment,
  Cause,
  CauseImage,
  CausePaymentChannel,
  CauseStatus,
  NumericInput,
} from '../types';

type NullableNumericPayload = number | NumericInput | null | undefined;

interface CausePayload {
  title?: string;
  description?: string;
  goalAmount?: number | string | '';
  images?: CauseImage[];
  externalProgramId?: string;
  rank?: NumericInput | null;
  slug?: string;
  shortDescription?: string;
  category?: string;
  tags?: string | string[];
  status?: CauseStatus;
  featured?: boolean;
  location?: string;
  startDate?: string;
  endDate?: string;
  organizer?: string;
  seoTitle?: string;
  seoDescription?: string;
  coverImageUrl?: string;
  titleEn?: string;
  titleAr?: string;
  descriptionTitle?: string;
  descriptionTitleEn?: string;
  descriptionTitleAr?: string;
  descriptionDetails?: string;
  descriptionDetailsEn?: string;
  descriptionDetailsAr?: string;
  goalAmountUsd?: NullableNumericPayload;
  goalAmountEgp?: NullableNumericPayload;
  raisedAmountUsd?: NullableNumericPayload;
  raisedAmountEgp?: NullableNumericPayload;
  mealPriceUsd?: NullableNumericPayload;
  mealPriceEgp?: NullableNumericPayload;
  message?: string;
  messageAr?: string;
  messageEn?: string;
  colorPrimary?: string;
  colorSecondary?: string;
  colorThird?: string;
  isZakatEligible?: boolean;
  raisedPercentage?: NullableNumericPayload;
  createdById?: string;
  updatedById?: string;
  published?: boolean;
  shareable?: boolean;
  isSpecial?: boolean;
  shareText?: string;
  shareTextAr?: string;
  shareTextEn?: string;
  minAmountEgp?: NullableNumericPayload;
  minAmountUsd?: NullableNumericPayload;
  suggestedDonationAmountsEgp?: number[] | string;
  mealType?: string;
  specialType?: string;
  familiesHelped?: NullableNumericPayload;
  mealsDistributed?: NullableNumericPayload;
  numberDonors?: NullableNumericPayload;
  isItemization?: boolean;
  donationEmailMessage?: string;
  donationSmsMessage?: string;
  donationsEnabled?: boolean;
  preferredPaymentChannel?: CausePaymentChannel;
  apsEnvironment?: ApsEnvironment;
  apsCommand?: ApsCommand;
  apsPaymentOption?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

type UnknownRecord = Record<string, unknown>;

const STATUS_VALUES: CauseStatus[] = [
  'draft',
  'published',
  'active',
  'paused',
  'archived',
];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizeStatus = (value: unknown): CauseStatus => {
  if (typeof value === 'string') {
    const matched = STATUS_VALUES.find(
      (status) => status === value.toLowerCase()
    );
    if (matched) {
      return matched;
    }
  }

  return 'draft';
};

const splitTags = (tags?: string | string[]) => {
  if (!tags) {
    return [];
  }

  if (Array.isArray(tags)) {
    return tags.map((tag) => tag.trim()).filter(Boolean);
  }

  return tags
    .split(',')
    .map((tag: string) => tag.trim())
    .filter(Boolean);
};

const toImageList = (images: unknown): CauseImage[] => {
  if (!Array.isArray(images)) {
    return [];
  }

  return images.reduce<CauseImage[]>((accumulator, image, index) => {
    if (typeof image === 'string') {
      accumulator.push({
        id: `image-${index}`,
        url: image,
        isPrimary: index === 0,
      });
      return accumulator;
    }

    if (typeof image === 'object' && image && 'url' in image) {
      const typedImage = image as {
        id?: string;
        url?: string;
        isPrimary?: boolean;
      };

      if (typedImage.url) {
        accumulator.push({
          id: typedImage.id || `image-${index}`,
          url: typedImage.url,
          isPrimary: typedImage.isPrimary ?? index === 0,
        });
      }
    }

    return accumulator;
  }, []);
};

const asRecord = (value: unknown): UnknownRecord => {
  if (typeof value === 'object' && value && !Array.isArray(value)) {
    return value as UnknownRecord;
  }

  return {};
};

const extractPayloadRecord = (rawPayload: unknown) => asRecord(rawPayload);

const extractAdminMetadata = (rawPayload: unknown) => {
  const payloadRecord = extractPayloadRecord(rawPayload);
  return asRecord(payloadRecord.adminMetadata);
};

const getMetadataValue = (rawPayload: unknown, ...keys: string[]) => {
  const adminMetadata = extractAdminMetadata(rawPayload);
  const payloadRecord = extractPayloadRecord(rawPayload);

  for (const key of keys) {
    const adminValue = adminMetadata[key];
    if (adminValue !== undefined && adminValue !== null) {
      return adminValue;
    }
  }

  for (const key of keys) {
    if (key === 'adminMetadata') {
      continue;
    }

    const payloadValue = payloadRecord[key];
    if (payloadValue !== undefined && payloadValue !== null) {
      return payloadValue;
    }
  }

  return undefined;
};

const getRawOrMetadataValue = (raw: UnknownRecord, rawPayload: unknown, ...keys: string[]) => {
  for (const key of keys) {
    const value = raw[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return getMetadataValue(rawPayload, ...keys);
};

const toStringValue = (rawPayload: unknown, ...keys: string[]) => {
  const value = getMetadataValue(rawPayload, ...keys);
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const toRawOrStringValue = (
  raw: UnknownRecord,
  rawPayload: unknown,
  ...keys: string[]
) => {
  const value = getRawOrMetadataValue(raw, rawPayload, ...keys);
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const toNumberValue = (rawPayload: unknown, ...keys: string[]) => {
  const value = getMetadataValue(rawPayload, ...keys);
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const toRawOrNumberValue = (
  raw: UnknownRecord,
  rawPayload: unknown,
  ...keys: string[]
) => {
  const value = getRawOrMetadataValue(raw, rawPayload, ...keys);
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const toBooleanValue = (
  rawPayload: unknown,
  keys: string[],
  fallback = false
) => {
  const value = getMetadataValue(rawPayload, ...keys);

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes'].includes(normalized)) {
      return true;
    }

    if (['false', '0', 'no'].includes(normalized)) {
      return false;
    }
  }

  return fallback;
};

const toRawOrBooleanValue = (
  raw: UnknownRecord,
  rawPayload: unknown,
  keys: string[],
  fallback = false
) => {
  const value = getRawOrMetadataValue(raw, rawPayload, ...keys);

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes'].includes(normalized)) {
      return true;
    }

    if (['false', '0', 'no'].includes(normalized)) {
      return false;
    }
  }

  return fallback;
};

const trimString = (value?: string) => value?.trim() || '';

const toNullableNumber = (value: NullableNumericPayload) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toEnumValue = <T extends string>(
  rawPayload: unknown,
  keys: string[],
  allowedValues: readonly T[],
  fallback: T | ''
): T | '' => {
  const value = getMetadataValue(rawPayload, ...keys);

  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value.trim().toUpperCase();
  const matched = allowedValues.find(
    (allowedValue) => allowedValue.toUpperCase() === normalized
  );

  return matched || fallback;
};

const extractCauseList = (data: unknown): unknown[] => {
  if (Array.isArray(data)) {
    return data;
  }

  const record = asRecord(data);
  if (Array.isArray(record.results)) {
    return record.results;
  }

  if (Array.isArray(record.items)) {
    return record.items;
  }

  return [];
};

const fetchCauseList = async (client: typeof apiClient): Promise<Cause[]> => {
  const response = await client.get('/causes');
  return extractCauseList(response.data).map(toCause);
};

const fetchCauseById = async (
  client: typeof apiClient,
  id: string
): Promise<Cause> => {
  const response = await client.get(`/causes/${id}`);
  return toCause(response.data);
};

const toCause = (raw: any): Cause => {
  const rawRecord = asRecord(raw);
  const images = toImageList(raw.images);
  const description = raw.description || '';
  const rawPayload = raw.rawPayload;
  const status = normalizeStatus(raw.status);
  const primaryGoal = Number(raw.goalAmount || 0);
  const primaryRaised = Number(raw.currentAmount || 0);
  const donationSettings = asRecord(raw.donationSettings);
  const suggestedDonationAmountsEgp = Array.isArray(
    donationSettings.suggestedDonationAmountsEgp
  )
    ? donationSettings.suggestedDonationAmountsEgp
        .map(Number)
        .filter((amount) => Number.isFinite(amount) && amount > 0)
    : [];

  return {
    id: raw.id,
    externalProgramId: String(raw.externalProgramId || '').trim(),
    rank:
      typeof raw.rank === 'number' && Number.isFinite(raw.rank)
        ? raw.rank
        : toRawOrNumberValue(rawRecord, rawPayload, 'rank'),
    title: raw.title || '',
    titleEn: toRawOrStringValue(rawRecord, rawPayload, 'titleEn', 'title_en'),
    titleAr: toRawOrStringValue(rawRecord, rawPayload, 'titleAr', 'title_ar'),
    slug:
      raw.slug ||
      toRawOrStringValue(rawRecord, rawPayload, 'slug') ||
      slugify(raw.title || raw.id || ''),
    shortDescription:
      toRawOrStringValue(rawRecord, rawPayload, 'shortDescription', 'short_description') ||
      description.slice(0, 140),
    description,
    descriptionTitle:
      toRawOrStringValue(rawRecord, rawPayload, 'descriptionTitle', 'description_title') ||
      '',
    descriptionTitleEn: toRawOrStringValue(
      rawRecord,
      rawPayload,
      'descriptionTitleEn',
      'description_title_en'
    ),
    descriptionTitleAr: toRawOrStringValue(
      rawRecord,
      rawPayload,
      'descriptionTitleAr',
      'description_title_ar'
    ),
    descriptionDetails:
      toRawOrStringValue(rawRecord, rawPayload, 'descriptionDetails', 'description_details') ||
      description,
    descriptionDetailsEn: toRawOrStringValue(
      rawRecord,
      rawPayload,
      'descriptionDetailsEn',
      'description_details_en'
    ),
    descriptionDetailsAr: toRawOrStringValue(
      rawRecord,
      rawPayload,
      'descriptionDetailsAr',
      'description_details_ar'
    ),
    goalAmount: primaryGoal,
    goalAmountUsd: toRawOrNumberValue(rawRecord, rawPayload, 'goalAmountUsd', 'goal_amount_usd'),
    goalAmountEgp:
      toRawOrNumberValue(rawRecord, rawPayload, 'goalAmountEgp', 'goal_amount_egp') ?? primaryGoal,
    currentAmount: primaryRaised,
    raisedAmountUsd:
      toRawOrNumberValue(rawRecord, rawPayload, 'raisedAmountUsd', 'raised_amount_usd'),
    raisedAmountEgp:
      toRawOrNumberValue(rawRecord, rawPayload, 'raisedAmountEgp', 'raised_amount_egp') ??
      primaryRaised,
    mealPriceUsd: toRawOrNumberValue(rawRecord, rawPayload, 'mealPriceUsd', 'meal_price_usd'),
    mealPriceEgp: toRawOrNumberValue(rawRecord, rawPayload, 'mealPriceEgp', 'meal_price_egp'),
    images,
    category: toRawOrStringValue(rawRecord, rawPayload, 'category') || 'General',
    tags: splitTags(
      getRawOrMetadataValue(rawRecord, rawPayload, 'tags', 'tag_list') as string[] | string | undefined
    ),
    status,
    featured: toRawOrBooleanValue(rawRecord, rawPayload, ['featured'], false),
    location: toRawOrStringValue(rawRecord, rawPayload, 'location'),
    startDate: toRawOrStringValue(rawRecord, rawPayload, 'startDate', 'start_date'),
    endDate: toRawOrStringValue(rawRecord, rawPayload, 'endDate', 'end_date'),
    organizer: toRawOrStringValue(rawRecord, rawPayload, 'organizer'),
    seoTitle: toRawOrStringValue(rawRecord, rawPayload, 'seoTitle', 'seo_title'),
    seoDescription: toRawOrStringValue(rawRecord, rawPayload, 'seoDescription', 'seo_description'),
    coverImageUrl:
      toRawOrStringValue(rawRecord, rawPayload, 'coverImageUrl', 'cover_image_url') ||
      images[0]?.url ||
      '',
    message: toStringValue(rawPayload, 'message'),
    messageAr: toStringValue(rawPayload, 'messageAr', 'message_ar'),
    messageEn: toStringValue(rawPayload, 'messageEn', 'message_en'),
    colorPrimary: toStringValue(rawPayload, 'colorPrimary', 'color_primary'),
    colorSecondary: toStringValue(rawPayload, 'colorSecondary', 'color_secondary'),
    colorThird: toStringValue(rawPayload, 'colorThird', 'color_third'),
    isZakatEligible: toBooleanValue(
      rawPayload,
      ['isZakatEligible', 'is_zakat_eligible'],
      false
    ),
    raisedPercentage:
      toNumberValue(rawPayload, 'raisedPercentage', 'raised_percentage'),
    createdById: toStringValue(rawPayload, 'createdById', 'created_by_id'),
    updatedById: toStringValue(rawPayload, 'updatedById', 'updated_by_id'),
    published: toBooleanValue(rawPayload, ['published'], status === 'published'),
    shareable: toBooleanValue(rawPayload, ['shareable'], false),
    isSpecial: toBooleanValue(rawPayload, ['isSpecial', 'is_special'], false),
    shareText: toStringValue(rawPayload, 'shareText', 'share_text'),
    shareTextAr: toStringValue(rawPayload, 'shareTextAr', 'share_text_ar'),
    shareTextEn: toStringValue(rawPayload, 'shareTextEn', 'share_text_en'),
    minAmountEgp: toNumberValue(rawPayload, 'minAmountEgp', 'min_amount_egp'),
    minAmountUsd: toNumberValue(rawPayload, 'minAmountUsd', 'min_amount_usd'),
    suggestedDonationAmountsEgp,
    mealType: toStringValue(rawPayload, 'mealType', 'meal_type'),
    specialType: toStringValue(rawPayload, 'specialType', 'special_type'),
    familiesHelped:
      toNumberValue(rawPayload, 'familiesHelped', 'families_helped'),
    mealsDistributed:
      toNumberValue(rawPayload, 'mealsDistributed', 'meals_distributed'),
    numberDonors: toNumberValue(rawPayload, 'numberDonors', 'number_donors'),
    isItemization: toBooleanValue(
      rawPayload,
      ['isItemization', 'is_itemization'],
      false
    ),
    donationEmailMessage: toStringValue(
      rawPayload,
      'donationEmailMessage',
      'donation_email_message'
    ),
    donationSmsMessage: toStringValue(
      rawPayload,
      'donationSmsMessage',
      'donation_sms_message'
    ),
    donationsEnabled: toBooleanValue(
      rawPayload,
      ['donationsEnabled', 'donations_enabled'],
      true
    ),
    preferredPaymentChannel: toEnumValue(
      rawPayload,
      ['preferredPaymentChannel', 'preferred_payment_channel'],
      ['aps_native', 'hosted_webview'] as const,
      'aps_native'
    ) as CausePaymentChannel,
    apsEnvironment: toEnumValue(
      rawPayload,
      ['apsEnvironment', 'aps_environment'],
      ['TEST', 'PRODUCTION'] as const,
      ''
    ),
    apsCommand: toEnumValue(
      rawPayload,
      ['apsCommand', 'aps_command'],
      ['AUTHORIZATION', 'PURCHASE'] as const,
      ''
    ),
    apsPaymentOption: toStringValue(
      rawPayload,
      'apsPaymentOption',
      'aps_payment_option'
    ),
    createdAt: raw.createdAt || '',
    updatedAt: raw.updatedAt || '',
    deletedAt: raw.deletedAt || '',
  };
};

const buildCausePayload = (causeData: CausePayload) => {
  const images = toImageList(causeData.images);
  const title = trimString(causeData.title);
  const description = trimString(causeData.description);
  const status = normalizeStatus(causeData.status);

  return {
    title,
    description,
    goalAmount: Number(causeData.goalAmount) || 0,
    images: images
      .map((image) => image.url.trim())
      .filter((image) => image.length > 0),
    externalProgramId: trimString(causeData.externalProgramId),
    rank: toNullableNumber(causeData.rank),
    slug: trimString(causeData.slug || slugify(title)),
    status,
    shortDescription: trimString(causeData.shortDescription),
    category: trimString(causeData.category),
    tags: splitTags(causeData.tags),
    featured: Boolean(causeData.featured),
    location: trimString(causeData.location),
    startDate: causeData.startDate || '',
    endDate: causeData.endDate || '',
    organizer: trimString(causeData.organizer),
    seoTitle: trimString(causeData.seoTitle),
    seoDescription: trimString(causeData.seoDescription),
    coverImageUrl: trimString(causeData.coverImageUrl) || images[0]?.url || '',
    titleEn: trimString(causeData.titleEn),
    titleAr: trimString(causeData.titleAr),
    descriptionTitle: trimString(causeData.descriptionTitle),
    descriptionTitleEn: trimString(causeData.descriptionTitleEn),
    descriptionTitleAr: trimString(causeData.descriptionTitleAr),
    descriptionDetails: trimString(causeData.descriptionDetails),
    descriptionDetailsEn: trimString(causeData.descriptionDetailsEn),
    descriptionDetailsAr: trimString(causeData.descriptionDetailsAr),
    goalAmountUsd: toNullableNumber(causeData.goalAmountUsd),
    goalAmountEgp: toNullableNumber(causeData.goalAmountEgp),
    raisedAmountUsd: toNullableNumber(causeData.raisedAmountUsd),
    raisedAmountEgp: toNullableNumber(causeData.raisedAmountEgp),
    mealPriceUsd: toNullableNumber(causeData.mealPriceUsd),
    mealPriceEgp: toNullableNumber(causeData.mealPriceEgp),
    message: trimString(causeData.message),
    messageAr: trimString(causeData.messageAr),
    messageEn: trimString(causeData.messageEn),
    colorPrimary: trimString(causeData.colorPrimary),
    colorSecondary: trimString(causeData.colorSecondary),
    colorThird: trimString(causeData.colorThird),
    isZakatEligible: Boolean(causeData.isZakatEligible),
    raisedPercentage: toNullableNumber(causeData.raisedPercentage),
    createdById: trimString(causeData.createdById),
    updatedById: trimString(causeData.updatedById),
    published:
      causeData.published !== undefined
        ? Boolean(causeData.published)
        : status === 'published',
    shareable: Boolean(causeData.shareable),
    isSpecial: Boolean(causeData.isSpecial),
    shareText: trimString(causeData.shareText),
    shareTextAr: trimString(causeData.shareTextAr),
    shareTextEn: trimString(causeData.shareTextEn),
    minAmountEgp: toNullableNumber(causeData.minAmountEgp),
    minAmountUsd: toNullableNumber(causeData.minAmountUsd),
    suggestedDonationAmountsEgp: Array.isArray(causeData.suggestedDonationAmountsEgp)
      ? causeData.suggestedDonationAmountsEgp
      : String(causeData.suggestedDonationAmountsEgp || '')
          .split(',')
          .map((amount) => Number(amount.trim()))
          .filter((amount) => Number.isFinite(amount) && amount > 0),
    mealType: trimString(causeData.mealType),
    specialType: trimString(causeData.specialType),
    familiesHelped: toNullableNumber(causeData.familiesHelped),
    mealsDistributed: toNullableNumber(causeData.mealsDistributed),
    numberDonors: toNullableNumber(causeData.numberDonors),
    isItemization: Boolean(causeData.isItemization),
    donationEmailMessage: trimString(causeData.donationEmailMessage),
    donationSmsMessage: trimString(causeData.donationSmsMessage),
    donationsEnabled:
      causeData.donationsEnabled !== undefined
        ? Boolean(causeData.donationsEnabled)
        : true,
    preferredPaymentChannel:
      causeData.preferredPaymentChannel === 'hosted_webview'
        ? 'hosted_webview'
        : 'aps_native',
    apsEnvironment:
      causeData.apsEnvironment === 'TEST' || causeData.apsEnvironment === 'PRODUCTION'
        ? causeData.apsEnvironment
        : '',
    apsCommand:
      causeData.apsCommand === 'AUTHORIZATION' || causeData.apsCommand === 'PURCHASE'
        ? causeData.apsCommand
        : '',
    apsPaymentOption: trimString(causeData.apsPaymentOption),
    deletedAt: causeData.deletedAt || '',
  };
};

export const CauseService = {
  getCauses: async (): Promise<Cause[]> => fetchCauseList(apiClient),

  getCauseById: async (id: string): Promise<Cause> =>
    fetchCauseById(apiClient, id),

  createCause: async (causeData: CausePayload): Promise<Cause> => {
    const response = await apiClient.post('/causes', buildCausePayload(causeData));
    return toCause(response.data);
  },

  updateCause: async (id: string, causeData: CausePayload): Promise<Cause> => {
    const response = await apiClient.put(`/causes/${id}`, buildCausePayload(causeData));
    return toCause(response.data);
  },

  deleteCause: async (id: string): Promise<void> => {
    await apiClient.delete(`/causes/${id}`);
  },

  publishCause: async (id: string): Promise<Cause> =>
    CauseService.updateCause(id, {
      status: 'published',
      published: true,
      deletedAt: '',
    }),

  unpublishCause: async (id: string): Promise<Cause> =>
    CauseService.updateCause(id, {
      status: 'draft',
      published: false,
      deletedAt: '',
    }),

  archiveCause: async (id: string): Promise<Cause> =>
    CauseService.updateCause(id, {
      status: 'archived',
      published: false,
      deletedAt: new Date().toISOString(),
    }),
};
